/**
 * tickdoc brand mark — hand-drawn SVG so it stays crisp at any size.
 *
 * Variants:
 *   - "full"  : wordmark + hand mark + tagline    (default, header / sidebar)
 *   - "compact": just the hand mark in a parchment circle (favicon-sized)
 *
 * Colour token: uses `currentColor` for the wordmark + accent, so the
 * component inherits the caller's text colour. The gold accent is fixed
 * at the brand hex so the crown colour is consistent everywhere.
 */
import React from "react";

const GOLD = "#D4AF37";
const INK = "currentColor";

export function TickdocLogo({ variant = "full", className = "", title = "tickdoc" }) {
  if (variant === "compact") {
    return (
      <svg
        viewBox="0 0 64 64"
        xmlns="http://www.w3.org/2000/svg"
        role="img"
        aria-label={title}
        className={className}
      >
        <title>{title}</title>
        {/* Outer ring */}
        <circle cx="32" cy="32" r="29" fill="none" stroke={INK} strokeWidth="2" />
        {/* Inner minute track */}
        <circle cx="32" cy="32" r="24" fill="none" stroke={INK} strokeWidth="0.6" opacity="0.35" />
        {/* Hour ticks — 12 / 3 / 6 / 9 accented in gold */}
        {[0, 90, 180, 270].map((a) => (
          <line
            key={a}
            x1="32" y1="6"
            x2="32" y2="10"
            stroke={GOLD}
            strokeWidth="2.2"
            strokeLinecap="round"
            transform={`rotate(${a} 32 32)`}
          />
        ))}
        {[30, 60, 120, 150, 210, 240, 300, 330].map((a) => (
          <line
            key={a}
            x1="32" y1="7"
            x2="32" y2="9.5"
            stroke={INK}
            strokeWidth="1.2"
            strokeLinecap="round"
            transform={`rotate(${a} 32 32)`}
          />
        ))}
        {/* Hour hand — pointing to 10 */}
        <line x1="32" y1="32" x2="22" y2="20" stroke={INK} strokeWidth="2.4" strokeLinecap="round" />
        {/* Minute hand — pointing to 2 */}
        <line x1="32" y1="32" x2="46" y2="22" stroke={INK} strokeWidth="1.8" strokeLinecap="round" />
        {/* Second hand in gold */}
        <line x1="32" y1="32" x2="36" y2="48" stroke={GOLD} strokeWidth="1" strokeLinecap="round" />
        {/* Pinion */}
        <circle cx="32" cy="32" r="2.2" fill={GOLD} />
        <circle cx="32" cy="32" r="0.9" fill={INK} />
      </svg>
    );
  }

  // Full wordmark + hand mark — tightened layout with larger, darker type
  return (
    <svg
      viewBox="0 0 380 110"
      xmlns="http://www.w3.org/2000/svg"
      role="img"
      aria-label={title}
      className={className}
    >
      <title>{title}</title>
      <defs>
        <linearGradient id="td-stroke" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={GOLD} stopOpacity="0.95" />
          <stop offset="100%" stopColor={GOLD} stopOpacity="0.6" />
        </linearGradient>
      </defs>

      {/* Watch-face mark — larger, tucked against the wordmark */}
      <g transform="translate(6,16)">
        <circle cx="38" cy="38" r="36" fill="none" stroke={INK} strokeWidth="2" />
        <circle cx="38" cy="38" r="31" fill="none" stroke={INK} strokeWidth="0.5" opacity="0.35" />
        {/* Hour pips — gold accents at cardinal points */}
        {[0, 90, 180, 270].map((a) => (
          <line
            key={a}
            x1="38" y1="6"
            x2="38" y2="12"
            stroke={GOLD}
            strokeWidth="2.6"
            strokeLinecap="round"
            transform={`rotate(${a} 38 38)`}
          />
        ))}
        {[30, 60, 120, 150, 210, 240, 300, 330].map((a) => (
          <line
            key={a}
            x1="38" y1="7"
            x2="38" y2="11"
            stroke={INK}
            strokeWidth="1.2"
            strokeLinecap="round"
            transform={`rotate(${a} 38 38)`}
          />
        ))}
        {/* Hands — classic 10:10 pose */}
        <line x1="38" y1="38" x2="25" y2="23" stroke={INK} strokeWidth="2.8" strokeLinecap="round" />
        <line x1="38" y1="38" x2="56" y2="25" stroke={INK} strokeWidth="2" strokeLinecap="round" />
        {/* Pinion */}
        <circle cx="38" cy="38" r="2.6" fill={GOLD} />
        {/* Crown top */}
        <rect x="35.5" y="-1" width="5" height="4" rx="1" fill={INK} />
      </g>

      {/* Wordmark "tickdoc" — larger (86px), heavier (600) for darker strokes */}
      <text
        x="92"
        y="70"
        fontFamily="'Playfair Display', 'Cormorant Garamond', 'Cardo', Georgia, serif"
        fontSize="86"
        fontWeight="600"
        fill={INK}
        letterSpacing="-2.4"
      >
        tick<tspan fill={INK} fontStyle="italic" fontWeight="700">doc</tspan>
      </text>

      {/* Gold hairline "case edge" */}
      <line x1="92" y1="82" x2="374" y2="82" stroke="url(#td-stroke)" strokeWidth="1" />

      {/* Tagline — bigger and bolder for readability */}
      <text
        x="92"
        y="100"
        fontFamily="'Inter', 'Helvetica Neue', Arial, sans-serif"
        fontSize="13"
        fontWeight="600"
        letterSpacing="5.5"
        fill={INK}
        opacity="0.9"
      >
        BUY · SELL · RESTORE
      </text>
    </svg>
  );
}

export default TickdocLogo;
